<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>

  <div class="container mt-4 mb-5">
    <div style="max-width:760px">
      <h3 class="mb-1">RustDesk Config</h3>
      <div style="font-style: italic; color:var(--bo-muted)">
        Fjernstyring av enhetsflåten (selvhostet RustDesk). Lagres i Couchbase; endringer tas i bruk uten
        restart. Se <code>docs/remote-control-rustdesk-runbook.md</code>.
      </div>

      <div class="alert alert-warning mt-3 py-2" style="font-size:0.88rem">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
        <strong>SoftPOS-advarsel:</strong> RustDesk (skjermopptak + accessibility-input) kan blokkere
        betalinger i Viva/Softpay (PCI MPoC). Ikke legg til POS-roller i «Tillatte roller» før PSP-en har
        godkjent det skriftlig. Fail-closed standard: av + kun KIOSK,KDS.
      </div>

      <config-lock ref="lock" :configured="isConfigured">
      <!-- The project's own switch: backoffice.css sets `.form-switch input { display:none }`
           and draws the toggle from the <i>, so Bootstrap's form-check markup renders nothing. -->
      <div class="form-switch-wrapper mt-3" style="clear:both; overflow:auto; float:none">
        <label class="form-switch">
          <div class="form-switch-container">
            <input type="checkbox" v-model="form.enabled" />
            <i></i>
            <span>Aktiver fjernstyring
          <span style="color:var(--bo-muted); font-size:0.82rem">— av = «Fjern-styring»-handlingen skjules i MDM-konsollet.</span></span>
          </div>
        </label>
      </div>

      <div class="row g-3 mt-1">
        <div class="col-md-7">
          <label class="form-label mb-1 rd-label">Connect-URL-mal</label>
          <input class="form-control" type="text" v-model="form.connectUrlTemplate"
                 placeholder="rustdesk://connect/{id}" />
          <span style="color:var(--bo-muted); font-size:0.78rem">
            <code>{id}</code> byttes med enhetens registrerte RustDesk-ID (leses av og lagres per enhet ved
            staging — standardklienten genererer ID-en selv). <code>{serial}</code> = serienummer (kun for en
            ev. fremtidig klient med deterministisk ID). Deep link åpner teknikerens lokale RustDesk; en Pro
            web-klient-URL (f.eks. <code>https://rustdesk.cagista.com/web?id={id}</code>) åpner i nettleser.
          </span>
        </div>
        <div class="col-md-5">
          <label class="form-label mb-1 rd-label">Tillatte policyer</label>
          <fieldset class="rd-roles">
            <div v-if="!policies.length" style="color:var(--bo-muted); font-size:0.82rem">
              Ingen policyer funnet — opprett policyer i CAGISTA MDM → Policyer først.
            </div>
            <label v-for="p in policies" :key="p.name" class="multiple-checkbox-label d-block">
              <input class="multiple-checkbox" type="checkbox" :value="p.name" v-model="selectedRoles" />
              <code>{{ p.name }}</code>
              <span style="color:var(--bo-muted); font-size:0.78rem"> — {{ p.label }}</span>
            </label>
          </fieldset>
          <span style="color:var(--bo-muted); font-size:0.78rem">
            Kun valgte policyer viser «Fjern-styring» (ingen valgt = skjult overalt — fail-closed). Velg
            også vedlikeholdspolicyen for fjernstyring under vedlikehold. NB: ikke velg policyer for
            enheter som tar betaling på enheten (SoftPOS) før PSP-en har godkjent det.
          </span>
        </div>
      </div>

      <div class="row g-3 mt-1">
        <div class="col-md-5">
          <label class="form-label mb-1 rd-label">Relay-host</label>
          <input class="form-control" type="text" v-model="form.relayHost"
                 placeholder="rustdesk.cagista.com" />
          <span style="color:var(--bo-muted); font-size:0.78rem">
            Bakes inn i hver staget enhet via QR-en — <strong>må aldri endres</strong> (endring = re-staging
            av hele flåten).
          </span>
        </div>
        <div class="col-md-7">
          <label class="form-label mb-1 rd-label">Relay public key</label>
          <input class="form-control" type="text" v-model="form.relayKey"
                 placeholder="innholdet i id_ed25519.pub på relay-noden" />
          <span style="color:var(--bo-muted); font-size:0.78rem">
            Den <em>offentlige</em> nøkkelen (ikke hemmelig — alle klienter får den). Den private halvdelen
            (<code>id_ed25519</code>) blir værende på relay-noden + backup.
          </span>
        </div>
      </div>

      <div class="mt-4 d-flex align-items-center gap-3">
        <button class="bo_btn" @click="save" :disabled="saving">{{ saving ? 'Lagrer…' : 'Lagre' }}</button>
        <span v-if="message" :class="error ? 'text-danger' : 'text-success'">{{ message }}</span>
      </div>
      </config-lock>

      <!-- Staging-QR — OUTSIDE the config-lock: it is an artifact of the SAVED config (rendered only from
           persisted values, never from unsaved edits), and must stay full-opacity/scannable while locked. -->
      <div class="mt-4">
        <h5 class="mb-1">Staging-QR (enhetsoppsett)</h5>
        <div v-if="qrDataUrl" class="rd-staging">
          <img :src="qrDataUrl" alt="RustDesk staging-QR" class="rd-staging-qr" />
          <div>
            <div style="font-size:0.85rem; color:var(--bo-ink-2)">
              Skann denne i RustDesk-appen på enheten ved staging — samme QR for hele flåten:
            </div>
            <ol class="rd-staging-steps">
              <li>Skann QR-en i RustDesk-appen (Innstillinger → ID/Relay-server → QR-import).</li>
              <li>Aktiver «RustDesk Input» under Tilgjengelighet (Android 13+: tillat først begrensede
                  innstillinger via Appinfo → ⋮).</li>
              <li>Godkjenn skjermopptak og sett <strong>permanent passord</strong> (uovervåket tilgang).</li>
              <li>Les av enhetens RustDesk-ID og registrer den i MDM-konsollet (⋮ → «RustDesk-ID …»).</li>
            </ol>
            <code style="font-size:0.75rem; word-break:break-all">{{ qrString }}</code>
          </div>
        </div>
        <div v-else style="color:var(--bo-muted); font-size:0.85rem">
          Fyll inn relay-host + relay public key og lagre — QR-en genereres fra de lagrede verdiene.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import ConfigLock from "@/components/other/ConfigLock";
import SettingsService from "@/services/settings/SettingsService";
import MdmService from "@/services/system/MdmService";
import QRCode from "qrcode";

// Same labels as MdmPolicies' Policytype dropdown.
const KIND_LABELS = {
  CUSTOM_LAUNCHER: "Egen launcher",
  MULTI_APP: "Fler-app Home (Google Launcher)",
  SINGLE_APP: "Enkelt-app kiosk",
  MAINTENANCE: "Vedlikehold"
};

export default {
  name: "RustdeskConfig",
  components: { LoadingSpinner, ConfigLock },
  computed: {
    // Already configured (→ starts locked): remote control has been activated. The URL-template and
    // allowed-roles fields always carry defaults, so `enabled` is the only signal a real setup exists.
    isConfigured() { return !!this.form.enabled; }
  },
  data() {
    return {
      loading: true,
      form: { enabled: false, connectUrlTemplate: "rustdesk://connect/{id}", allowedRoles: "",
              relayHost: "", relayKey: "" },
      // The dynamic (blank-start) policy list — checkbox picker; selectedRoles <-> form.allowedRoles (CSV).
      policies: [],
      selectedRoles: [],
      // Staging QR — rendered ONLY from persisted relayHost/relayKey (after load/save), never live edits.
      qrDataUrl: "",
      qrString: "",
      saving: false,
      message: "",
      error: false
    };
  },
  mounted() {
    this.loadConfig();
    this.loadPolicies();
  },
  methods: {
    loadConfig() {
      SettingsService.getRustdeskConfig().then(
        (response) => {
          const data = response.data || {};
          Object.keys(this.form).forEach((k) => {
            if (data[k] !== undefined && data[k] !== null) this.form[k] = data[k];
          });
          this.selectedRoles = (this.form.allowedRoles || "").split(",").map((s) => s.trim()).filter(Boolean);
          this.mergeStoredRoles();
          this.renderQr();
          this.loading = false;
        },
        (error) => {
          console.log(error);
          this.loading = false;
          this.error = true;
          this.message = this.errMsg(error, "Kunne ikke hente konfigurasjon.");
        }
      );
    },
    // Policies are a blank-start dynamic list (no reserved names) — offer the ACTUAL policy names as
    // checkboxes instead of free text. _PROV variants are internal and excluded.
    loadPolicies() {
      MdmService.getPolicySettings().then(
        (res) => {
          const kinds = (res.data && res.data.kinds) || {};
          this.policies = Object.keys(kinds)
            .filter((name) => !name.endsWith("_PROV"))
            .sort()
            .map((name) => ({ name, label: KIND_LABELS[kinds[name]] || kinds[name] || "" }));
          this.mergeStoredRoles();
        },
        (error) => { console.log(error); }   // fail-soft: page still works without the list
      );
    },
    // A stored role whose policy was deleted must stay visible so it can be unchecked.
    mergeStoredRoles() {
      this.selectedRoles.forEach((r) => {
        if (!this.policies.some((p) => p.name === r)) {
          this.policies.push({ name: r, label: "finnes ikke lenger" });
        }
      });
    },
    save() {
      this.saving = true;
      this.message = "";
      this.error = false;
      this.form.allowedRoles = this.selectedRoles.join(",");
      SettingsService.saveRustdeskConfig(this.form).then(
        (response) => {
          this.saving = false;
          const data = response.data || {};
          Object.keys(this.form).forEach((k) => {
            if (data[k] !== undefined && data[k] !== null) this.form[k] = data[k];
          });
          this.selectedRoles = (this.form.allowedRoles || "").split(",").map((s) => s.trim()).filter(Boolean);
          this.renderQr();   // the save response = persisted values
          this.error = false;
          this.message = "Lagret ✓ — tas i bruk umiddelbart (ingen restart).";
          this.$refs.lock.relock();   // re-lock after a successful save
        },
        (error) => {
          this.saving = false;
          this.error = true;
          this.message = this.errMsg(error, "Kunne ikke lagre konfigurasjon.");
        }
      );
    },
    // The staging QR encodes the RustDesk client config-import string — same content for every device.
    renderQr() {
      const host = (this.form.relayHost || "").trim();
      const key = (this.form.relayKey || "").trim();
      if (!host || !key) {
        this.qrDataUrl = "";
        this.qrString = "";
        return;
      }
      this.qrString = "config=" + JSON.stringify({ host: host, key: key });
      QRCode.toDataURL(this.qrString, { width: 220, margin: 1 })
        .then((url) => { this.qrDataUrl = url; })
        .catch((e) => { console.log(e); this.qrDataUrl = ""; });
    },
    errMsg(error, fallback) {
      const data = error && error.response ? error.response.data : null;
      return (data && data.error) || fallback;
    }
  }
};
</script>

<style scoped>
.rd-label {
  font-size: 0.85rem;
  color: var(--bo-muted);
}
.rd-roles {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid var(--bo-line);
  border-radius: 6px;
  padding: 8px 12px;
}
.rd-staging {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  border: 1px solid var(--bo-line);
  border-radius: 6px;
  padding: 12px;
}
.rd-staging-qr {
  width: 220px;
  height: 220px;
  flex: 0 0 auto;
  image-rendering: pixelated;   /* keep QR modules crisp when scaled */
}
.rd-staging-steps {
  font-size: 0.82rem;
  color: var(--bo-ink-2);
  margin: 6px 0 8px;
  padding-left: 1.2rem;
}
.rd-staging-steps li {
  margin-bottom: 2px;
}
@media (max-width: 768px) {
  .rd-staging {
    flex-direction: column;
    align-items: center;
  }
}
</style>
